import {useField} from 'formik';
import {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    AssignmentSelectorsFormik, AssignmentSelectorsFormikProps, UserSelectorFormikProps, Tab, Tabs, UserSelectorFormik
} from 'components';
import {usePrevious} from 'hooks';
import {AccountAssignmentInfo} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

type Props = {
    assignmentSelectorProps: AssignmentSelectorsFormikProps & {
        emptyValue?: any
    };
    userSelectorProps: UserSelectorFormikProps;
}

export const AssignmentUserSelectorFormik: React.FC<Props> = ({
    assignmentSelectorProps: {emptyValue: assignmentEmptyValue = {}, ...assignmentSelectorProps},
    userSelectorProps
}: Props) => {
    const {t} = useTranslation();
    const assignmentTabName = useMemo(() => t(T_KEYS.ASSIGNMENT), [t]);
    const userTabName = useMemo(() => t(T_KEYS.AGENT), [t]); // small confusion between user/agent names
    const fallbackTabName = useMemo(() => assignmentTabName, [assignmentTabName]);

    const [{value: assignmentValue}, , {setValue: setAssignmentValue}] = useField(assignmentSelectorProps.name);
    const [{value: userValue}, , {setValue: setUserValue}] = useField(userSelectorProps.name);

    const [initialized, setInitialized] = useState(false);
    const [defaultTabName, setDefaultTabName] = useState(null);
    const prevTabName = usePrevious(defaultTabName);
    const [prevAssignmentValue, setPrevAssignmentValue] = useState<AccountAssignmentInfo>(assignmentValue);
    const [prevUserValue, setPrevUserValue] = useState(userValue);

    const onChange = useCallback(({name}: {name: string}) => {
        if (name === assignmentTabName) {
            setPrevUserValue(userValue);
            setUserValue(null);
            setAssignmentValue(prevAssignmentValue);
            assignmentSelectorProps?.onChange?.(prevAssignmentValue?.groupId, prevAssignmentValue?.subgroupId);
        } else if (name === userTabName) {
            setPrevAssignmentValue(assignmentValue);
            setAssignmentValue(assignmentEmptyValue);
            setUserValue(prevUserValue);
            assignmentSelectorProps?.onChange?.(null, null);
        }
    }, [assignmentSelectorProps, assignmentValue, setAssignmentValue, userValue, setUserValue, prevAssignmentValue, prevUserValue,
        userTabName, assignmentTabName, assignmentEmptyValue]);

    useEffect(() => {
        if (!defaultTabName) {
            if (assignmentValue?.groupId) {
                setDefaultTabName(assignmentTabName);
            } else if (userValue) {
                setDefaultTabName(userTabName);
            } else {
                setDefaultTabName(fallbackTabName);
            }
        }
        if (!initialized) {
            setInitialized(true);
        }

    }, [defaultTabName, userValue, assignmentValue?.groupId, userTabName, assignmentTabName, fallbackTabName, initialized, onChange]);

    useEffect(() => {
        if (defaultTabName && defaultTabName !== prevTabName) {
            onChange({name: defaultTabName});
        }
    }, [defaultTabName, onChange, prevTabName]);

    if (!initialized) {
        return null;
    }

    return (
        <Tabs stretch contentIndent={false} defaultTabName={defaultTabName} onChange={onChange}>
            <Tab name={assignmentTabName} disabled={isEmpty(assignmentSelectorProps.groups)}>
                <AssignmentSelectorsFormik grid {...assignmentSelectorProps} />
            </Tab>
            <Tab name={userTabName}>
                <UserSelectorFormik {...userSelectorProps} />
            </Tab>
        </Tabs>
    );
};
